import type React from "react"
import { useTranslation } from "react-i18next"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material"
import {
  Close as CloseIcon,
} from "@mui/icons-material"
import EntityList from "../../components/EntityList"
import api from "../../services/api"
import { Tenant, TenantType } from "@/src/interfaces"
import HorseLoader from "../../components/loadingHorse"
import { useIsMobile, useIsTablet } from "../../hooks/useMediaQuery"
import { useCryptoNavigation } from "../../App"


// Interface para TenantType com seus tenants
interface TenantTypeWithTenants {
  tenantTypeId: number;
  tenantTypeName: string;
  tenants: Tenant[];
  totalItems: number;
}

// Interface para a estrutura da resposta da API
interface TenantApiResponse {
  tenants: Tenant[];
  [key: string]: any;
}

// Row type para as funções que recebem rows
interface TenantTypeRow {
  tenantTypeId: number;
  tenantTypeName: string;
  totalItems: number;
  tenants?: Tenant[];
  [key: string]: any;
}

export default function TenantTypeList() {
  const { t } = useTranslation()
  console.log("🔄 TenantTypeList render");
  
  const { navigateToPage } = useCryptoNavigation()
  
  const theme = useTheme()
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  // Estado para controlar o modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<TenantTypeWithTenants | null>(null);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [tenantsData, setTenantsData] = useState<Tenant[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Memoizar funções de navegação
  const handleNew = useCallback(() => {
    console.log("🚀 Navegando para novo tipo de proprietário");
    navigateToPage('tipos', { mode: 'new' });
  }, [navigateToPage]);

  const handleEdit = useCallback((tenantTypeId: number | string) => {
    console.log("✏️ Navegando para editar tipo de proprietário ID:", tenantTypeId);
    navigateToPage('tipos', { mode: 'edit', id: String(tenantTypeId) });
  }, [navigateToPage]);

  // Função para navegar para a lista de proprietários de um tipo específico
  const handleNavigateToTenants = useCallback((tenantTypeName: string) => {
    console.log(`📋 Navegando para proprietários do tipo ${tenantTypeName}`);
    navigateToPage('proprietarios', { mode: 'list', filter: `type:${tenantTypeName}` });
  }, [navigateToPage]);

  // Carregar todos os tenants ao montar o componente
  useEffect(() => {
    const fetchAllTenants = async () => {
      try {
        console.log("📊 Carregando todos os tenants...");
        const response = await api.get<TenantApiResponse>("/tenant");
        if (response.data && response.data.tenants) {
          setTenantsData(response.data.tenants);
          console.log("✅ Tenants carregados:", response.data.tenants.length);
        }
      } catch (error) {
        console.error("❌ Erro ao carregar todos os tenants:", error);
      }
    };

    fetchAllTenants();
  }, []);

  // Função para encontrar um tenant completo pelo ID
  const findFullTenantById = useCallback((tenantId: number | string | undefined): Tenant | undefined => {
    if (tenantId === undefined) return undefined;
    
    // Converte para string para facilitar a comparação
    const idStr = String(tenantId).toLowerCase();
    
    return tenantsData.find(tenant => {
      const tenantIdStr = tenant.tenantId ? String(tenant.tenantId).toLowerCase() : '';
      const tenantIDStr = tenant.tenantID ? String(tenant.tenantID).toLowerCase() : '';
      
      return tenantIdStr === idStr || tenantIDStr === idStr;
    });
  }, [tenantsData]);

  // Função para abrir o modal e carregar os tenants de um tipo
  const handleShowTenants = useCallback(async (row: TenantTypeRow) => {
    // Só mostrar o modal se tiver tenants relacionados
    if (!row.totalItems || row.totalItems === 0) {
      console.log("❌ Nenhum tenant relacionado encontrado");
      return;
    }
    
    try {
      setLoadingTenants(true);
      console.log("🔍 Carregando tenants do tipo:", row.tenantTypeName);
      
      // Verificar se já temos os dados dos tenants
      if (row.tenants && row.tenants.length > 0) {
        console.log("📊 Usando dados já carregados");
        // Enriquecer os dados dos tenants com os dados completos
        const enrichedTenants = row.tenants.map(tenant => {
          const fullTenant = findFullTenantById(tenant.tenantId || tenant.tenantID);
          return fullTenant || tenant; // Usar os dados completos se encontrados
        });
        
        const enrichedType: TenantTypeWithTenants = {
          tenantTypeId: row.tenantTypeId,
          tenantTypeName: row.tenantTypeName,
          tenants: enrichedTenants,
          totalItems: row.totalItems
        };
        
        setSelectedType(enrichedType);
        setSelectedTenant(enrichedTenants[0]); // Selecionar o primeiro tenant por padrão
        setModalOpen(true);
      } else {
        console.log("🔍 Buscando detalhes do tipo via API...");
        // Buscar detalhes do tipo de tenant com seus tenants relacionados
        const response = await api.get<TenantTypeWithTenants>(`/tenant_type/${row.tenantTypeId}`);
        console.log("📦 Dados detalhados do tipo:", response.data);
        
        if (response.data && response.data.tenants && response.data.tenants.length > 0) {
          // Enriquecer os dados dos tenants com os dados completos
          const enrichedTenants = response.data.tenants.map(tenant => {
            const fullTenant = findFullTenantById(tenant.tenantId || tenant.tenantID);
            return fullTenant || tenant; // Usar os dados completos se encontrados
          });
          
          const enrichedType: TenantTypeWithTenants = {
            ...response.data,
            tenants: enrichedTenants
          };
          
          setSelectedType(enrichedType);
          setSelectedTenant(enrichedTenants[0]); // Selecionar o primeiro tenant por padrão
          setModalOpen(true);
          console.log("✅ Modal aberto com dados enriquecidos");
        }
      }
    } catch (error) {
      console.error("❌ Erro ao carregar tenants relacionados:", error);
    } finally {
      setLoadingTenants(false);
    }
  }, [findFullTenantById]);

  // Função para fechar o modal
  const handleCloseModal = useCallback(() => {
    console.log("❌ Fechando modal");
    setModalOpen(false);
    setSelectedType(null);
    setSelectedTenant(null);
    setTabValue(0);
  }, []);

  // Função para mudar de tenant nos tabs
  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (selectedType && selectedType.tenants && selectedType.tenants[newValue]) {
      setSelectedTenant(selectedType.tenants[newValue]);
      console.log("🔄 Mudando para tenant:", selectedType.tenants[newValue].tenantName);
    }
  }, [selectedType]);

  // Função para visualizar detalhes
  const handleViewDetails = useCallback((tenantTypes: TenantTypeRow) => {
    console.log("👁️ Visualizando detalhes do tipo:", tenantTypes.tenantTypeName);
    handleShowTenants(tenantTypes);
  }, [handleShowTenants]);

  // Função para deletar tipo
  const handleDelete = useCallback(async (tenantTypeId: number) => {
    if (!window.confirm(t("tenantTypes.confirmDelete"))) {
      return
    }

    try {
      console.log("🗑️ Excluindo tipo de proprietário:", tenantTypeId);
      await api.delete(`/tenant_type/${tenantTypeId}`)
      console.log("✅ Tipo de proprietário excluído com sucesso")
      
      if (modalOpen) {
        setModalOpen(false)
      }
      
      setRefreshTrigger((prev) => prev + 1)
    } catch (err: any) {
      console.error("❌ Erro ao excluir tipo de proprietário:", err)
      const errorMessage = err.response?.data?.message || "Erro desconhecido";
      alert(`Ocorreu um erro ao excluir o tipo de proprietário: ${errorMessage}`)
    }
  }, [modalOpen]);

  // Definindo colunas incluindo a coluna de total com clique
  const columns = useMemo(() => [
    { 
      id: "tenantTypeName", 
      label: t("fields.name"), 
      minWidth: 170,
      alternativeField: "name" 
    },
    { 
      id: "totalItems", 
      label: t("tenants.ownersCount"), 
      minWidth: 120,
      align: "center" as const,
      format: (value: number, row: TenantTypeRow) => {
        const hasRelatedTenants = value && value > 0;
        
        // Se tem tenants relacionados, retornar um valor clicável
        if (hasRelatedTenants) {
          return (
            <Typography 
              component="span" 
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                fontWeight: 'medium',
                '&:hover': { 
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleShowTenants(row);
              }}
            >
              {value}
            </Typography>
          );
        }
        
        // Se não tem tenants, retornar um valor não clicável
        return value || 0;
      }
    }
  ], [handleShowTenants]);

  // Componente para exibir um campo de dados com label
  const DataField = ({ label, value }: { label: string, value: any }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
        {label}
      </Typography>
      <Typography variant="body1">
        {value || '-'}
      </Typography>
    </Box>
  );

  return (
    <>
      <EntityList
        title={t("nav.tenantTypes")}
        endpoint="/tenant_type"
        columns={columns}
        addPath="" // Não usado com navegação criptografada
        editPathPrefix="" // Não usado com navegação criptografada
        idField="tenantTypeId"
        hideViewAction={true}
        onRowClick={handleViewDetails}
        refreshTrigger={refreshTrigger}
        useEncryptedNavigation={true}
        encryptedPageName="tipos"
        onAddClick={handleNew}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
      />

      {/* Modal para exibir tenants relacionados */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: 2, sm: 3 },
          }}
        >
          <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
            Proprietários do tipo: {selectedType?.tenantTypeName}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleCloseModal} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingTenants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <HorseLoader />
            </Box>
          ) : selectedType?.tenants && selectedType.tenants.length > 0 ? (
            <>
              {/* Tabs para selecionar diferentes tenants */}
              <Tabs
                value={tabValue}
                onChange={handleChangeTab}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                {selectedType.tenants.map((tenant, index) => {
                  // Usar tenantId ou tenantID, dependendo do que estiver disponível
                  const id = tenant.tenantId || tenant.tenantID;
                  return (
                    <Tab 
                      key={String(id)} 
                      label={tenant.tenantName || `Proprietário ${index + 1}`} 
                      id={`tenant-tab-${index}`} 
                    />
                  );
                })}
              </Tabs>
              
              {/* Exibição dos detalhes do tenant selecionado */}
              {selectedTenant && (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <DataField label={t("fields.id")} value={selectedTenant.tenantId || selectedTenant.tenantID} />
                      <DataField label={t("fields.name")} value={selectedTenant.tenantName} />
                      <DataField label={t("fields.nickname")} value={selectedTenant.tenantNickName} />
                      <DataField label={t("fields.phone")} value={selectedTenant.tenantPhone} />
                      <DataField label={t("fields.fiscalCode")} value={selectedTenant.tenantFiscalCode} />
                      <DataField label={t("fields.type")} value={selectedTenant.tenantTypeName} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DataField label={t("fields.address")} value={selectedTenant.tenantAddress} />
                      <DataField label={t("fields.city")} value={selectedTenant.tenantCity} />
                      <DataField label={t("fields.state")} value={selectedTenant.tenantState} />
                      <DataField label={t("fields.zipcode")} value={selectedTenant.tenantZipcode} />
                      <DataField label={t("fields.country")} value={selectedTenant.tenantCountry} />
                    </Grid>
                  </Grid>
                  
                  {/* Botão para navegar para detalhes do tenant */}
                  {/* <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        handleCloseModal();
                        const tenantId = selectedTenant.tenantId || selectedTenant.tenantID;
                        navigateToPage('proprietarios', { mode: 'edit', id: String(tenantId) });
                      }}
                    >
                      {t("common.viewFullDetails")}
                    </Button>
                  </Box> */}
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body1" sx={{ p: 2 }}>
              Não foram encontrados proprietários relacionados a este tipo.
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Button onClick={handleCloseModal} color="primary">
            Fechar
          </Button>
          {selectedType && selectedType.totalItems > 0 && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                handleCloseModal();
                handleNavigateToTenants(selectedType.tenantTypeName);
              }}
            >
              Ver Todos na Lista
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
  
}